'use client';

import Link from 'next/link';
import { useAuth } from '@clerk/nextjs';
import { ArrowLeft, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LandingFaqAccordion } from '@/components/landing/landing-faq';
import { LandingFooter } from '@/components/landing/landing-footer';
import { Button } from '@/components/ui/button';
import { LogoIcon } from '@/components/shared/logo-icon';
import { useLanguage } from '@/lib/translation';
import { cn } from '@/lib/utils';

export function FaqLandingPageContent() {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const { isSignedIn, isLoaded } = useAuth();
  const showPublicBackBar = isLoaded && !isSignedIn;
  const showPublicFooter = isLoaded && !isSignedIn;
  const items = t('landing.faqPage.items', {
    returnObjects: true,
  }) as Array<{ question: string; answer: string }>;

  if (!items || items.length === 0) {
    return null;
  }

  const supportWhatsAppHref = `https://wa.me/972515918930?text=${encodeURIComponent(
    `R: ${t('landing.faqPage.whatsappPrefillMessage')}`,
  )}`;

  return (
    <div
      className={cn(
        'flex min-h-[100dvh] flex-col overflow-x-hidden bg-gradient-to-br from-green-100 via-green-50 to-green-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900',
        isRTL ? 'rtl' : 'ltr',
      )}
    >
      {showPublicBackBar ? (
        <div className="border-b border-emerald-100/70 bg-white/90 px-4 py-5 pt-[max(1.25rem,env(safe-area-inset-top))] dark:border-emerald-900/60 dark:bg-gray-950/90 sm:px-6 sm:py-6">
          <div className="mx-auto flex w-full max-w-4xl items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <Button
                asChild
                size="lg"
                className="h-12 w-full max-w-md touch-manipulation bg-green-600 px-6 text-base font-semibold text-white hover:bg-green-700 sm:w-auto sm:px-8"
              >
                <Link href="/" className="inline-flex items-center justify-center gap-2">
                  <ArrowLeft className={cn('h-5 w-5 shrink-0', isRTL && 'rotate-180')} aria-hidden />
                  {t('landing.faqPage.backToHome')}
                </Link>
              </Button>
            </div>
            <Link
              href="/"
              className="shrink-0 touch-manipulation"
              aria-label={t('landing.hero.brandName')}
            >
              <LogoIcon className="h-10 w-10 sm:h-11 sm:w-11" />
            </Link>
          </div>
        </div>
      ) : null}

      <section className="relative z-10 flex-1 border-b border-emerald-100/70 bg-white/80 px-4 py-10 dark:border-emerald-900/60 dark:bg-gray-950/80 sm:px-6 sm:py-12 md:py-16">
        <div className="mx-auto w-full max-w-4xl">
          <LandingFaqAccordion
            items={items}
            layout="singleColumn"
            enableShowMore={false}
            titleKeyPrefix="landing.faqPage"
            idPrefix="faq-page"
          />

          <div className="mt-10 border-t border-emerald-100/70 pt-10 dark:border-emerald-900/60">
            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center text-sm text-muted-foreground">
              <Link
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-emerald-700 underline underline-offset-2 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300"
              >
                {t('landing.faqPage.legalPrivacy')}
              </Link>
              <span className="text-muted-foreground/80" aria-hidden>
                ·
              </span>
              <Link
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-emerald-700 underline underline-offset-2 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300"
              >
                {t('landing.faqPage.legalTerms')}
              </Link>
            </div>
          </div>
        </div>
      </section>
      {showPublicFooter ? (
        <div className="mt-auto w-full pb-[max(1rem,env(safe-area-inset-bottom))]">
          <LandingFooter />
        </div>
      ) : null}
    </div>
  );
}
